/**
 * Meetings — governance suite list. MC / AGM / SGM with the statutory clock:
 * an AGM-deadline banner (Sec. 75 — by 30 September) sits on top until the
 * AGM is actually held. Lives OUTSIDE /admin: the Secretary and committee
 * run meetings from their resident personas. Wired: GET /api/meetings.
 */
import { router } from 'expo-router';
import React from 'react';
import { View } from 'react-native';

import { radius, space } from '@bero/tokens';
import { goBack, SmallButton } from '@/features/account-admin/bits';
import { Icon } from '@/features/account-admin/icons';
import { useT, monthShort, dayShort } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Card, EmptyState, Pill, Screen, Skeleton, TopBar } from '@/ui';

export type MeetingRow = {
  id: number; kind: 'MC' | 'AGM' | 'SGM'; title: string; status: string;
  scheduledAt: string | null; venue: string;
  quorumRequired: number | null; presentCount: number | null; quorumMet: boolean | null;
};

const KIND_LABEL: Record<string, DictKey> = { MC: 'mtg_kind_mc', AGM: 'mtg_kind_agm', SGM: 'mtg_kind_sgm' };
export const STATUS_META: Record<string, { labelKey: DictKey; s: 'pending' | 'approved' | 'rejected' }> = {
  DRAFT: { labelKey: 'mtg_status_draft', s: 'pending' },
  NOTICED: { labelKey: 'mtg_status_noticed', s: 'approved' },
  HELD: { labelKey: 'mtg_status_held', s: 'approved' },
  MINUTES_DRAFT: { labelKey: 'mtg_status_minutes_due', s: 'pending' },
  MINUTES_CONFIRMED: { labelKey: 'mtg_status_closed', s: 'approved' },
  CANCELLED: { labelKey: 'mtg_status_cancelled', s: 'rejected' },
};

export function fmtMeetingWhen(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  let h = d.getHours(); const am = h < 12 ? 'am' : 'pm'; h = h % 12 || 12;
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${dayShort(d.getDay())}, ${d.getDate()} ${monthShort(d.getMonth())}, ${h}:${mm} ${am}`;
}

function fmtDMY(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getDate()} ${monthShort(d.getMonth())} ${d.getFullYear()}`;
}

type Agm = { deadline: string; daysLeft: number; agmHeld: boolean; citation: string };

export default function MeetingsScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const [items, setItems] = React.useState<MeetingRow[]>([]);
  const [agm, setAgm] = React.useState<Agm | null>(null);
  const [canWrite, setCanWrite] = React.useState(false);
  const [state, setState] = React.useState<'loading' | 'ready' | 'error' | 'forbidden'>('loading');

  const load = React.useCallback(async () => {
    setState('loading');
    try {
      const [body, me] = await Promise.all([
        api.get<{ items: MeetingRow[]; agm: Agm }>('/api/meetings'),
        api.get<{ perms?: Record<string, string[]> }>('/api/positions/me').catch(() => ({ perms: {} as Record<string, string[]> })),
      ]);
      setItems(body.items ?? []);
      setAgm(body.agm ?? null);
      setCanWrite((me.perms?.['governance.meetings'] ?? []).includes('write'));
      setState('ready');
    } catch (e) {
      setState((e as { status?: number })?.status === 403 ? 'forbidden' : 'error');
    }
  }, []);
  React.useEffect(() => { void Promise.resolve().then(() => load()); }, [load]);

  return (
    <Screen scroll={false}>
      <TopBar
        title={t('mtg_title')}
        onBack={goBack}
        right={canWrite ? <SmallButton label={t('nav_new')} icon="plus" onPress={() => router.push('/meetings/new')} /> : undefined}
      />
      <Screen padded style={{ paddingTop: space.s3 }}>
        {state === 'loading' ? (
          <View style={{ gap: space.s3 }}>{[0, 1, 2].map((i) => <Skeleton key={i} h={92} />)}</View>
        ) : state === 'forbidden' ? (
          <EmptyState
            icon={<Icon name="shield" size={40} color={colors.textTertiary} />}
            title={t('mtg_forbidden_title')}
            body={t('mtg_forbidden_body')}
          />
        ) : state === 'error' ? (
          <EmptyState
            icon={<Icon name="alert-circle" size={40} color={colors.textTertiary} />}
            title={t('mtg_err_title')}
            body={t('ct_check_connection')}
            cta={t('retry')}
            onCta={() => { void load(); }}
          />
        ) : (
          <>
            {agm && !agm.agmHeld ? (
              <View style={{
                borderRadius: radius.lg, borderWidth: 1, padding: space.s4, marginBottom: space.s4,
                borderColor: agm.daysLeft < 30 ? colors.danger500 : colors.accent500,
                backgroundColor: agm.daysLeft < 30 ? colors.danger50 : colors.accent100,
              }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2 }}>
                  <Icon name="clock" size={16} color={agm.daysLeft < 30 ? colors.danger500 : colors.accent700} />
                  <AppText variant="bodyMd" weight="600" style={{ flex: 1 }}>
                    {agm.daysLeft >= 0
                      ? t('mtg_agm_due', { days: agm.daysLeft, date: fmtDMY(agm.deadline) })
                      : t('mtg_agm_overdue', { days: -agm.daysLeft })}
                  </AppText>
                </View>
                <AppText variant="bodySm" style={{ marginTop: 4, color: colors.accent700 }}>
                  {agm.citation}
                </AppText>
              </View>
            ) : null}

            {items.length === 0 ? (
              <EmptyState
                icon={<Icon name="calendar" size={40} color={colors.textTertiary} />}
                title={t('mtg_empty_title')}
                body="Schedule the monthly committee meeting — the bye-laws expect at least one a month."
                cta={canWrite ? t('mtg_empty_cta') : undefined}
                onCta={canWrite ? () => router.push('/meetings/new') : undefined}
              />
            ) : (
              <View style={{ gap: space.s3 }}>
                {items.map((m) => {
                  const st = STATUS_META[m.status];
                  return (
                    <Card key={m.id} onPress={() => router.push(`/meetings/${m.id}`)}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2 }}>
                        <Pill status="pending" label={KIND_LABEL[m.kind] ? t(KIND_LABEL[m.kind]) : m.kind} />
                        <View style={{ flex: 1 }} />
                        <Pill status={st?.s ?? 'pending'} label={st ? t(st.labelKey) : m.status} />
                      </View>
                      <AppText variant="bodyMd" weight="600" style={{ marginTop: space.s2 }}>{m.title}</AppText>
                      <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
                        {fmtMeetingWhen(m.scheduledAt)} · {m.venue}
                        {m.quorumMet != null ? ` · ${t('mtg_quorum_count', { p: m.presentCount ?? 0, r: m.quorumRequired ?? 0 })}${m.quorumMet ? '' : ` — ${t('mtg_not_met')}`}` : ''}
                      </AppText>
                    </Card>
                  );
                })}
              </View>
            )}
          </>
        )}
      </Screen>
    </Screen>
  );
}
